import { XMarkIcon } from '@heroicons/react/24/outline';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

// A design terv modálja: sötétkék-tónusú scrim + blur, FELÜLRE igazított panel (8vh), 14px rádiusz (`rounded-2xl` = `--radius` + 6px), nagy puha
// árnyék. FELÉPÍTÉS (best practice — Linear/Stripe): a panel FIX magasság-korláttal `flex-col`, a `DialogHeader`
// (fent, border-b) és a `DialogFooter` (lent, border-t + muted háttér) RÖGZÍTETT (`shrink-0`), és KIZÁRÓLAG a
// `DialogBody` (közép) görög. A padding a szekciókon van (a panelen `p-0`). A záró X a HEADER sorában, a címmel
// függőlegesen egy vonalban (nincs absolute-hackelés).
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 app-fixed-fill fixed inset-0 z-50 bg-[rgba(10,13,24,0.45)] backdrop-blur-[6px]',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    {/* A VÍZSZINTES KÖZÉPRE IGAZÍTÁS flex-burkolóval, NEM `translate-x-[-50%]`-kel — és ez nem stílus-ízlés.
        A transform TARTALMAZÓ BLOKKOT csinál a `position: fixed` leszármazottaknak, így a modál
        `overflow-hidden`-je levágta a benne nyíló popovereket (mért hiba: a tömeges besorolás
        kategória-listájának a fele eltűnt). Transform nélkül a `fixed` popover a VIEWPORT-hoz igazodik, és
        az overflow nem nyúl hozzá — így a popover a modál DOM-fáján BELÜL maradhat, ami viszont a
        görgetéshez kell: a Dialog `react-remove-scroll`-ja a kívülre portálozott listát nem engedi görgetni
        (mért hiba: a munkatárs-választó legördülője nem volt görgethető).

        A burkoló `pointer-events-none`, a tartalom `pointer-events-auto`: a modálon kívüli kattintás így
        változatlanul az overlayre jut (a Radix azon zárja be a modált).

        `items-start`: a flex alapértelmezése (`stretch`) a modált a burkoló TELJES magasságára nyújtotta —
        minden modál a `max-h` plafonig nőtt, egy kétsoros megerősítés is (user-lelet, mobilon a teljes
        képernyőt kitöltötte). A modál a tartalmához méreteződik. `px-4`: mobilon 16 px térköz a képernyő
        szélétől (a `w-full` különben szélétől szélig futott); asztalon a `max-w-lg` miatt nem látszik. */}
    <div className="app-fixed-fill pointer-events-none fixed inset-0 z-50 flex items-start justify-center px-4 pt-[8vh]">
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'dark-surface-popover pointer-events-auto flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border p-0',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-top-2',
          className,
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </div>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

/**
 * RÖGZÍTETT fejléc (nem görög): border-b + a záró X a cím SORÁBAN, függőlegesen középre igazítva. A children
 * (cím/leírás/vezető-ikon) a bal oszlopban; a `leading` opcionális vezető elem (pl. tónus-ikon warning/danger modálhoz).
 */
function DialogHeader({
  className,
  children,
  leading,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { leading?: React.ReactNode }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        // Fix min-magasság + items-center → a header MINDEN modáltnál EGYFORMA magas (a lábléccel egyezően),
        // akár van tónus-ikon, akár nincs. SZÁNDÉKOSAN vonal + külön háttér NÉLKÜL (ugyanaz a felület, mint a
        // body) — iparági best practice (Linear/Stripe): a hierarchiát a tipográfia + a térköz adja, nem keret.
        // A ZÁRÓ X A CÍM SORÁRA igazodik, nem a fejléc-blokk közepére. MÉRT HIBA: `items-center` mellett a
        // fejléc a TELJES tartalom-blokkot centrálja — egy leírásos (kétsoros) fejlécnél tehát az X ~10 px-szel
        // a cím alá csúszott, pedig a komponens ígérete („a címmel egy vonalban") pont ez volt.
        //
        // MIÉRT `py-[1.125rem]` (18 px) ÉS `items-start`: így a cím felső éle MINDKÉT esetben 18 px — az
        // egysoros fejléc pixelre ugyanott marad, mint eddig (24 px cím + 2×18 px = a 60 px min-magasság),
        // a kétsoros pedig ugyanonnan indul. Az X `-mt-0.5`-je a 28 px-es gombot a 24 px-es cím-sor közepére
        // húzza. Középre-igazítással ez nem megoldható: a blokk magassága változó.
        'flex min-h-[3.75rem] shrink-0 items-start justify-between gap-4 px-6 py-[1.125rem]',
        className,
      )}
      {...props}
    >
      <div className="flex min-w-0 items-start gap-3">
        {leading}
        <div className="flex min-w-0 flex-col gap-1 text-left">{children}</div>
      </div>
      <DialogPrimitive.Close className="text-muted-foreground focus-visible:ring-ring hover:bg-accent hover:text-foreground -mr-1.5 -mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-md opacity-80 transition-colors hover:opacity-100 focus:outline-none focus-visible:ring-1 disabled:pointer-events-none">
        <XMarkIcon className="h-4 w-4" />
        <span className="sr-only">{t('common.close')}</span>
      </DialogPrimitive.Close>
    </div>
  );
}

/** A GÖRGŐ középső tartalom-régió — KIZÁRÓLAG ez scroll-ozik (a header/footer rögzített). NINCS minimum magasság:
 *  a rövid modal a tartalmához méreteződik (kompakt — best practice); a `min-h-0` KELL, hogy a görgő tartalomnál
 *  a flex-body a tartalom alá tudjon zsugorodni (enélkül a `min-height:auto` elrontaná az `overflow-y-auto`-t). */
function DialogBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>): JSX.Element {
  return <div className={cn('min-h-0 flex-1 overflow-y-auto px-6 py-4', className)} {...props} />;
}

/** RÖGZÍTETT lábléc (nem görög). Ugyanaz a felület, mint a body/header; SZÁNDÉKOSAN NINCS felső elválasztó vonal
 *  (a header alsó hairline-ja elég — letisztultabb). Fix min-magasság = a headeré (egységes). */
function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div
      className={cn(
        'flex min-h-[3.75rem] shrink-0 flex-col-reverse gap-2 px-6 py-3 sm:flex-row sm:items-center sm:justify-end',
        className,
      )}
      {...props}
    />
  );
}

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('truncate text-lg font-semibold leading-6 tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-md text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
